/** @jsx jsx */
import React, { useRef, useMemo } from 'react';
import { jsx } from '@emotion/core';

import { Box } from '@material-ui/core';
import PageContainer from 'components/PageContainer';
import ActionButtonsContainer, {
  TextAction,
} from 'components/ActionButtonsContainer';
import Title from 'components/Title';
import HorizontalRule from 'components/HorizontalRule';

import useTranslate from 'hooks/useTranslate';
import { Trans, useMarkdownTranslate } from 'hooks/useTranslate/useTranslate';

import RestartLink from '../../form/RestartLink';
import styles from '../styles';
import { conformToMask, decimalMoneyMask } from 'utils/masks';
import { InstallmentsOffer, SplitPayOffer } from 'state/financeOffers';
import InstallmentCheckout from './InstallmentCheckout';
import SplitPayCheckout from './SplitPayCheckout';
import isPresent from 'utils/isPresent';
import CreditCardExistingAccount from '../CreditCardExistingAccount';
import CreditCard from '../CreditCard';
import { FirstOffersEnum } from 'hooks/useQueryParameters';
import { FormikProps } from 'formik';

import { FormValues } from '../UnifiedPrequalOffer';

import { useDebounce } from 'use-debounce';
import CreditCardNotPreapproved from '../CreditCardNotPreapproved';
import { PrequalOffer } from 'state/prequalOffer';
import { useUnifiedIsInSession } from 'hooks/useWorkflow/unifiedPrequalCheckoutWorkflow';
import CreditCardFallback from 'features/unified/Offer/CheckoutOffer/CreditCardFallback';
import { ExpandedPanel } from 'state/unifiedWidgets';
import { useSelector } from 'react-redux';
import { selectTenant, isOfferConfigured } from 'state/tenant';

import { OfferWidgetType } from 'hooks/useAnalyticVariables';
import CancelButton from 'features/CancelButton';
import { map, prop } from 'ramda';

interface PropTypes {
  intendedPurchaseAmount: string | null;
  formikProps: FormikProps<FormValues>;
  installmentOffers: InstallmentsOffer[];
  splitPayOffers: SplitPayOffer[];
  prequalOffer: PrequalOffer;
  onPanelChange: (
    event: React.ChangeEvent<{}>,
    panelName: string,
    expanded: boolean,
  ) => void;
  expandedPanel: ExpandedPanel | undefined;
  firstOffers?: FirstOffersEnum;
  defaultCartAmount: number;
  hasExistingAccount?: boolean;
  openToBuy?: number;
  nextPath: string;
}

const UnifiedCheckoutOfferView: React.FC<PropTypes> = props => {
  const {
    intendedPurchaseAmount,
    defaultCartAmount,
    onPanelChange,
    expandedPanel,
    installmentOffers,
    splitPayOffers,
    prequalOffer,
    formikProps,
    hasExistingAccount,
    openToBuy,
    nextPath,
  } = props;

  const t = useTranslate();
  const tMd = useMarkdownTranslate();
  const inSession = useUnifiedIsInSession();
  const { bread } = useSelector(selectTenant);

  const splitPayPresent =
    isOfferConfigured('splitpay', bread) && isPresent(splitPayOffers);
  const installmentsPresent =
    isOfferConfigured('installments', bread) && isPresent(installmentOffers);

  const principal = useMemo(
    () =>
      intendedPurchaseAmount
        ? parseFloat(intendedPurchaseAmount)
        : defaultCartAmount,
    [defaultCartAmount, intendedPurchaseAmount],
  );

  const [principalDebounced] = useDebounce(principal, 200);

  const installmentId: OfferWidgetType = 'installments';
  const installmentMaxCapacity = useMemo(() => {
    const result = Math.max(...map(prop('capacityValue'), installmentOffers));
    return result ? result / 100 : undefined;
  }, [installmentOffers]);
  const installmentCapacityExceeded = useMemo(() => {
    return Boolean(
      installmentMaxCapacity && principalDebounced > installmentMaxCapacity,
    );
  }, [principalDebounced, installmentMaxCapacity]);

  const installments =
    installmentsPresent && !installmentCapacityExceeded ? (
      <div
        id={installmentId}
        key="installments"
        css={styles.paymentOptionContainer}
      >
        <InstallmentCheckout
          intendedPurchaseAmount={principalDebounced}
          panelName="installments"
          expanded={expandedPanel === 'installments'}
          onChange={onPanelChange}
          installmentOffers={installmentOffers}
        />
      </div>
    ) : null;

  const splitPayId: OfferWidgetType = 'splitpay';
  const splitpayCapacityExceeded = useMemo(() => {
    return (
      splitPayOffers[0] &&
      principalDebounced > splitPayOffers[0].capacityValue / 100
    );
  }, [principalDebounced, splitPayOffers]);

  const splitPay =
    splitPayPresent && !splitpayCapacityExceeded ? (
      <div
        id={splitPayId}
        key={splitPayOffers[0].id}
        css={styles.paymentOptionContainer}
      >
        <SplitPayCheckout
          intendedPurchaseAmount={principalDebounced}
          panelName={'splitpay'}
          expanded={expandedPanel === 'splitpay'}
          onChange={onPanelChange}
          splitPayOffer={splitPayOffers[0]}
        />
      </div>
    ) : null;

  let prequalWidgetType:
    | 'existingAccount'
    | 'prequal'
    | 'notQualified'
    | 'fallback'
    | undefined = undefined;
  if (hasExistingAccount) {
    prequalWidgetType = 'existingAccount';
  } else if (prequalOffer.prequalificationId && prequalOffer.hasOffer) {
    prequalWidgetType = 'prequal';
  } else if (prequalOffer.notQualified) {
    prequalWidgetType = 'notQualified';
  } else if (inSession) {
    prequalWidgetType = 'fallback';
  }

  const prequalificationId: OfferWidgetType = 'prequalOffer';
  const creditLimitExceeded = useMemo(() => {
    return Boolean(
      !prequalOffer.creditLimit ||
        (prequalOffer.creditLimit &&
          principalDebounced > prequalOffer.creditLimit),
    );
  }, [prequalOffer.creditLimit, principalDebounced]);

  const prequalification =
    isOfferConfigured('prequalification', bread) &&
    prequalWidgetType === 'prequal' &&
    !creditLimitExceeded ? (
      <div
        id={prequalificationId}
        key="credit"
        css={styles.paymentOptionContainer}
      >
        <CreditCard
          formikProps={formikProps}
          creditLimit={prequalOffer.creditLimit}
          expanded={expandedPanel === 'prequalification'}
          panelName={'prequalification'}
          handlePanelExpandChange={onPanelChange}
          intendedPurchaseAmount={principalDebounced}
          nextPath={nextPath}
        />
      </div>
    ) : null;

  const divider =
    (installments || splitPay) &&
    isOfferConfigured('prequalification', bread) &&
    prequalification ? (
      <div key="divider" css={styles.paymentOptionContainer}>
        <HorizontalRule orString={t('unified.orHorizontalRule')} />
      </div>
    ) : null;

  const unhappyPathDivider =
    installments || splitPay ? (
      <div key="divider" css={styles.paymentOptionContainer}>
        <HorizontalRule orString={t('unified.orHorizontalRule')} />
      </div>
    ) : null;

  const existingAccountWidget = isOfferConfigured('prequalification', bread) &&
    prequalWidgetType === 'existingAccount' && (
      <React.Fragment>
        <div css={styles.paymentOptionContainer}>
          <CreditCardExistingAccount
            expanded={expandedPanel === 'prequalification'}
            panelName={'prequalification'}
            handlePanelExpandChange={onPanelChange}
            openToBuy={openToBuy}
          />
        </div>
        {unhappyPathDivider}
      </React.Fragment>
    );

  const notQualifiedWidget = isOfferConfigured('prequalification', bread) &&
    prequalWidgetType === 'notQualified' && (
      <React.Fragment>
        {unhappyPathDivider}
        <div css={styles.paymentOptionContainer}>
          <CreditCardNotPreapproved
            formikProps={formikProps}
            nextPath={nextPath}
            expanded={expandedPanel === 'prequalification'}
            panelName={'prequalification'}
            handlePanelExpandChange={onPanelChange}
          />
        </div>
      </React.Fragment>
    );
  const firstOffers = useRef<ExpandedPanel | undefined>(expandedPanel);

  const creditCardFallBackWidget = isOfferConfigured(
    'prequalification',
    bread,
  ) &&
    prequalWidgetType === 'fallback' && (
      <React.Fragment>
        {unhappyPathDivider}
        <div css={styles.paymentOptionContainer}>
          <CreditCardFallback
            expanded={expandedPanel === 'prequalification'}
            panelName={'prequalification'}
            handlePanelExpandChange={onPanelChange}
          />
        </div>
      </React.Fragment>
    );

  const widgets = useMemo(() => {
    switch (firstOffers.current) {
      case 'installments':
        return [installments, splitPay, divider, prequalification];

      case 'splitpay':
        return [splitPay, installments, divider, prequalification];

      case 'prequalification':
        return [prequalification, divider, installments, splitPay];

      default:
        return [installments, splitPay, divider, prequalification];
    }
  }, [divider, firstOffers, installments, prequalification, splitPay]);

  // TODO: Move message to config
  // Fallback error message when there are no widgets to display
  const fallbackMessage = !splitPay &&
    !installments &&
    !prequalification &&
    !existingAccountWidget &&
    !notQualifiedWidget &&
    !creditCardFallBackWidget && (
      <React.Fragment>
        <div css={styles.subTitleText}>
          {t('unified.checkoutOffer.fallbackMessage')}
        </div>
      </React.Fragment>
    );

  return (
    <PageContainer maxWidth="sm">
      <Box mb={2}>
        <Title css={styles.promoText}>{tMd('unified.offer.title')}</Title>
      </Box>

      <div css={styles.subTitleText}>
        <div css={styles.offerSubtitle}>{tMd('unified.offer.subtitle')}</div>
        {tMd('unified.offer.intendedSpend')}{' '}
      </div>
      <Box mb={2} css={styles.fixedIntendedPurchaseAmount}>
        {conformToMask(principalDebounced.toFixed(2), decimalMoneyMask)}
      </Box>

      {existingAccountWidget}

      {widgets}

      {notQualifiedWidget}

      {creditCardFallBackWidget}

      {fallbackMessage}

      <ActionButtonsContainer>
        <CancelButton>{t('unified.offer.embeddedContinue')}</CancelButton>

        <TextAction>
          <div css={styles.restartText}>
            <Trans
              i18nKey="unified.offer.restartMessage"
              components={[<RestartLink />]}
            />
          </div>
        </TextAction>
      </ActionButtonsContainer>
    </PageContainer>
  );
};

export default UnifiedCheckoutOfferView;
